import { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, TrendingUp, Download, UserCog, Briefcase, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  employee: { label: "Salarié", color: "bg-blue-100 text-blue-800", Icon: UserCog },
  subcontractor: { label: "Sous-traitant", color: "bg-amber-100 text-amber-800", Icon: Briefcase },
};

export const WorkerProfitabilityPage = () => {
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState({ rows: [], totals: { total_hours: 0, total_labor_cost: 0, total_ca_share: 0, total_margin: 0 } });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const r = await api.getWorkerProfitability(params);
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const exportCsv = () => {
    if (!data.rows.length) { toast.info("Aucune donnée"); return; }
    const headers = ["Intervenant", "Type", "Heures", "Taux €/h", "Coût main-d'œuvre", "CA associé", "Marge", "Marge %", "Chantiers"];
    const rows = data.rows.map(r => [
      r.name,
      TYPE_LABELS[r.type]?.label || r.type,
      r.total_hours,
      r.hourly_rate,
      r.labor_cost,
      r.ca_share,
      r.margin,
      r.margin_percent,
      r.orders_count,
    ]);
    const csv = "\uFEFF" + [headers.join(";"), ...rows.map(r => r.map(v => String(v).replace(/;/g, ",")).join(";"))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `rentabilite_intervenants_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const missingRates = data.rows.filter(r => r.hourly_rate_missing).length;

  return (
    <div className="space-y-6 animate-in" data-testid="worker-profitability-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Rentabilité par intervenant</h1>
          <p className="text-muted-foreground mt-1">
            Heures saisies × taux horaire, part de CA pondérée par le temps passé sur chaque chantier.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="wp-export-csv">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Période</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Du</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="wp-start" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="wp-end" />
            </div>
            <Button onClick={fetchData} disabled={loading} data-testid="wp-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Actualiser"}
            </Button>
            {(start || end) && (
              <Button variant="ghost" onClick={() => { setStart(""); setEnd(""); setTimeout(fetchData, 0); }}>
                Effacer
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Heures totales</p>
          <p className="text-2xl font-heading font-bold mt-1 font-mono">{data.totals.total_hours} h</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Coût main-d'œuvre</p>
          <p className="text-2xl font-heading font-bold mt-1 text-red-700 font-mono">{formatCurrency(data.totals.total_labor_cost)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">CA associé (HT)</p>
          <p className="text-2xl font-heading font-bold mt-1 text-indigo-700 font-mono">{formatCurrency(data.totals.total_ca_share)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Marge totale</p>
          <p className={`text-2xl font-heading font-bold mt-1 font-mono ${data.totals.total_margin >= 0 ? "text-green-700" : "text-red-700"}`}>
            {formatCurrency(data.totals.total_margin)}
          </p>
        </CardContent></Card>
      </div>

      {missingRates > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4 pb-3 flex items-start gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <span>
              <strong>{missingRates}</strong> intervenant{missingRates > 1 ? "s" : ""} sans taux horaire défini — leur coût est à zéro, ce qui gonfle artificiellement la marge.
              Renseignez un salaire (onglet <em>Informations</em> du salarié) ou un taux horaire sur le sous-traitant.
            </span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Détail par intervenant</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : data.rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-8" data-testid="wp-empty">
              Aucune saisie de temps sur la période.
            </p>
          ) : (
            <Table data-testid="wp-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Intervenant</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Heures</TableHead>
                  <TableHead className="text-right">Taux €/h</TableHead>
                  <TableHead className="text-right">Coût</TableHead>
                  <TableHead className="text-right">CA associé</TableHead>
                  <TableHead className="text-right">Marge</TableHead>
                  <TableHead className="text-right">Marge %</TableHead>
                  <TableHead className="text-right">Chantiers</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => {
                  const typeCfg = TYPE_LABELS[r.type] || {};
                  const TypeIcon = typeCfg.Icon;
                  return (
                    <TableRow key={`${r.type}-${r.worker_id}`} data-testid={`wp-row-${r.worker_id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <Badge className={`${typeCfg.color || ""} text-xs`}>
                          {TypeIcon && <TypeIcon className="h-3 w-3 mr-1" />}
                          {typeCfg.label || r.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{r.total_hours} h</TableCell>
                      <TableCell className="text-right font-mono">
                        {r.hourly_rate_missing ? <span className="text-amber-600">—</span> : `${r.hourly_rate} €`}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-700">{formatCurrency(r.labor_cost)}</TableCell>
                      <TableCell className="text-right font-mono text-indigo-700">{formatCurrency(r.ca_share)}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${r.margin >= 0 ? "text-green-700" : "text-red-700"}`}>
                        {formatCurrency(r.margin)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{r.margin_percent}%</TableCell>
                      <TableCell className="text-right font-mono">{r.orders_count}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkerProfitabilityPage;
